import { useTranslation } from 'react-i18next';
import { useNotification } from '../../shared/notifications/NotificationProvider';

/**
 * Returns a share-handler tailored to opportunity posts. On platforms that
 * expose the Web Share API (mobile browsers, modern Safari) it pops the
 * native sheet; everywhere else it copies the URL to the clipboard and
 * surfaces a toast so the user knows the action succeeded.
 *
 * <p>The user cancelling the native sheet is treated as a no-op — we don't
 * surface a "cancelled" toast because the user already knows.</p>
 */
export function useShareOpportunity() {
  const { t } = useTranslation();
  const { notify } = useNotification();

  return async function share(opts: { postId: string; title: string; bodyPreview?: string }) {
    const url = `${window.location.origin}/opportunities/${opts.postId}`;
    const shareData: ShareData = {
      title: opts.title,
      text: opts.bodyPreview?.slice(0, 200),
      url,
    };

    // Some browsers (older Safari, Firefox desktop) expose `navigator.share`
    // but throw on call. Probe with `canShare` when available to avoid that.
    const canUseNativeShare =
      typeof navigator !== 'undefined'
      && typeof navigator.share === 'function'
      && (typeof navigator.canShare !== 'function' || navigator.canShare(shareData));

    if (canUseNativeShare) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        // AbortError = user cancelled the native sheet. Anything else, fall
        // back to clipboard so the action still completes.
        if ((err as DOMException)?.name === 'AbortError') return;
      }
    }

    // Clipboard fallback. In older / insecure contexts (http://), the
    // Clipboard API isn't available — degrade to a select-and-copy hint.
    try {
      await navigator.clipboard.writeText(url);
      notify.success(t('opportunities.share.copied'));
    } catch {
      notify.error(t('opportunities.share.failed'));
    }
  };
}
